import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, X, SlidersHorizontal, BookmarkPlus, Keyboard } from 'lucide-react';
import { MAIL_API, mailAuth, QUICK_FILTERS } from './mailUtils';
import { MailSearchBuilder } from './MailSearchBuilder';

/** Search box + quick chips + advanced builder + save search. Produces one Gmail query string. */
export const MailSearchBar = ({ q, setQ, onSearch, onClear, scope, setScope, inFolder, onSaved, onHelp, inputRef }) => {
  const [builder, setBuilder] = useState(false);
  const localRef = useRef(null);
  const ref = inputRef || localRef;

  const has = (frag) => q.split(/\s+/).includes(frag) || q.includes(frag);
  const toggleChip = (f) => {
    const next = has(f.q) ? q.replace(f.q, '').replace(/\s{2,}/g, ' ').trim() : `${q} ${f.q}`.trim();
    setQ(next); onSearch(next);
  };
  const save = async () => {
    const name = window.prompt('Name this search', q.slice(0, 40));
    if (!name) return;
    try { const r = await axios.post(`${MAIL_API}/saved-searches`, { name, q, scope, folder: inFolder }, mailAuth()); onSaved?.(r.data); toast.success(`Saved “${name}”`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  return (
    <div className="flex flex-col gap-2 w-full" data-testid="mail-search-bar">
      <form onSubmit={e => { e.preventDefault(); onSearch(q.trim()); }} className="flex items-center gap-2 w-full">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input ref={ref} value={q} onChange={e => setQ(e.target.value)} placeholder="Search — a WO like 26-3092, from:fike.com, has:attachment, newer_than:7d…"
            className="w-full pl-9 pr-24 py-2 rounded-xl bg-white border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400 shadow-sm" data-testid="mail-search-input" />
          <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
            {q && <button type="button" onClick={() => { setQ(''); onClear(); }} className="p-1 text-slate-400 hover:text-slate-900" title="Clear" data-testid="mail-search-clear"><X className="w-4 h-4" /></button>}
            {q && <button type="button" onClick={save} className="p-1 text-slate-400 hover:text-sky-700" title="Save this search" data-testid="mail-search-save"><BookmarkPlus className="w-4 h-4" /></button>}
            <button type="button" onClick={() => setBuilder(b => !b)} className={`p-1 rounded ${builder ? 'text-sky-700 bg-sky-50' : 'text-slate-400 hover:text-slate-900'}`} title="Advanced search" data-testid="mail-search-advanced"><SlidersHorizontal className="w-4 h-4" /></button>
          </div>
        </div>
        <select value={scope} onChange={e => setScope(e.target.value)} className="h-9 rounded-xl border border-slate-200 bg-white text-xs text-slate-700 px-2" title="Where to search" data-testid="mail-search-scope">
          <option value="all">All mail</option>
          <option value="folder">This folder</option>
        </select>
        <button type="button" onClick={onHelp} className="hidden md:inline-flex p-2 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-900" title="Keyboard shortcuts (?)" data-testid="mail-shortcuts-btn"><Keyboard className="w-4 h-4" /></button>
      </form>
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="mail-quick-filters">
        {QUICK_FILTERS.map(f => { const on = has(f.q); return (
          <button key={f.id} type="button" onClick={() => toggleChip(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${on ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white/80 border-slate-200 text-slate-700 hover:border-sky-300'}`} data-testid={`mail-quick-${f.id}`}>{f.label}</button>); })}
      </div>
      {builder && <MailSearchBuilder initial={q} onApply={(built) => { setQ(built); onSearch(built); setBuilder(false); }} onClose={() => setBuilder(false)} />}
    </div>
  );
};
